import type { ReactNode } from 'react';
/*
 * `Link` de `core/rotas`, e não de `react-router-dom`.
 *
 * O painel mora sob `/:slug`. Com o `Link` cru, "Voltar para a Home" apontava
 * para `/home` e "Ver plano" para `/settings/billing` - dois endereços que não
 * casam com rota nenhuma. Ou seja: a tela de erro levava a outra tela de erro,
 * e a conta suspensa não tinha como chegar no lugar de resolver a suspensão.
 */
import { Link } from '../../core/rotas';
import { useAuth } from '../../core/auth';
import { Button } from '../../ui';
import { Icone } from '../../ui/icones';
import orbita from '../../ds/assets/hub/grafismos/yardhub-orbita.svg';
import './estados.css';

/**
 * A tela de estado na linguagem do Hub: um cartão claro em duas colunas, o
 * texto à esquerda (código pequeno, `h1`, explicação, ações e uma nota que
 * diz o que fazer a seguir) e a arte à direita, onde o número vira forma
 * grande fora da leitura. Nada aqui é dado inventado: as ações são as rotas
 * reais (`/home`, `/settings/billing`), "voltar" é o histórico do navegador e
 * "tentar de novo" recarrega a página.
 */
/**
 * O cartão de estado, puro: só markup e props, nenhum hook, nenhum roteador.
 * É o que o `ErrorBoundary` usa quando o contexto de autenticação ou o
 * roteador podem estar quebrados, e o que as telas de rota embrulham com
 * os links de verdade.
 */
export function CartaoDeEstado({
  code,
  rotuloDoCodigo,
  icone,
  title,
  description,
  nota,
  acoes,
}: {
  code: string;
  /** O que o selo pequeno diz por escrito ("Erro 404"); o número grande é só desenho. */
  rotuloDoCodigo: string;
  /** O ícone que o documento pede em todo cartão de estado (seção 3). */
  icone: ReactNode;
  title: ReactNode;
  description: string;
  /** Uma linha de orientação prática abaixo das ações. */
  nota?: ReactNode;
  acoes?: ReactNode;
}) {
  return (
    <div className="state state-hub" data-codigo={code}>
      <div className="state-hub__cartao">
        <div className="state-hub__texto">
          {/* Ícone E código por escrito. `ROTAS-AUTH.md`, seção 3, pede que
              403, 500 e conta suspensa tenham "ícone, frase e uma ação -
              nunca tela em branco". O número sozinho é jargão de protocolo:
              o selo diz "Erro 404" por extenso, e o cadeado diz o resto. */}
          <span className="state-hub__codigo">
            <span className="ic-mini" aria-hidden="true">{icone}</span>
            {rotuloDoCodigo}
          </span>
          {/* `h1`, e não `h2`: 403, 404, 500 e a conta suspensa são páginas
              inteiras, e cada página precisa do seu `h1`. */}
          <h1 className="state__titulo">{title}</h1>
          <p>{description}</p>
          {acoes && <div className="state-hub__acoes">{acoes}</div>}
          {nota && <p className="state-hub__nota">{nota}</p>}
        </div>
        {/* A arte sai inteira do leitor de tela: o número grande e a órbita
            são forma, o texto ao lado já disse tudo. */}
        <div className="state-hub__arte" aria-hidden="true">
          <span className="state-hub__grao" />
          <img src={orbita} alt="" />
          <div className="code">
            {code.length > 1 ? <>{code.slice(0, -1)}<mark>{code.slice(-1)}</mark></> : <mark>{code}</mark>}
          </div>
        </div>
      </div>
    </div>
  );
}

/** A tela de estado com as ações de rota: primária por `Link` de `core/rotas`, mais a secundária. */
function State({
  to = '/home',
  cta = 'Ir para o painel',
  secundaria,
  semPrimaria = false,
  ...resto
}: {
  code: string;
  rotuloDoCodigo: string;
  icone: ReactNode;
  title: ReactNode;
  description: string;
  nota?: ReactNode;
  to?: string;
  cta?: string;
  secundaria?: ReactNode;
  /** Sem ação principal: quando o único destino levaria de volta ao mesmo bloqueio. */
  semPrimaria?: boolean;
}) {
  return (
    <CartaoDeEstado
      {...resto}
      acoes={
        <>
          {!semPrimaria && (
            <Link to={to}>
              <Button>{cta}</Button>
            </Link>
          )}
          {secundaria}
        </>
      }
    />
  );
}

/** Volta uma página no histórico; só existe se houver para onde voltar. */
function Voltar() {
  if (typeof window === 'undefined' || window.history.length <= 1) return null;
  return (
    <Button variant="secondary" onClick={() => window.history.back()}>
      Voltar à página anterior
    </Button>
  );
}

/** 403 é distinto de vazio: o usuário precisa entender o que houve (doc 18.3). */
export const Forbidden = () => (
  <State
    code="403"
    rotuloDoCodigo="Erro 403"
    icone={<Icone.Cadeado tamanho={13} />}
    title={<>Esta área <mark>não está liberada</mark> para você.</>}
    description="Sua conta entrou, mas o seu papel não inclui esta tela, ou o produto não está contratado nesta conta. Nada foi perdido."
    nota="Se for permissão, um administrador da conta ajusta o seu papel em Configurações, Usuários. Se for produto, quem administra a conta fala com a equipe Yard."
    secundaria={<Voltar />}
  />
);

export const NotFound = () => (
  <State
    code="404"
    rotuloDoCodigo="Erro 404"
    icone={<Icone.Busca tamanho={13} />}
    title={<>Esse endereço <mark>não existe</mark> por aqui.</>}
    description="Pode ter sido removido, o link pode estar incompleto ou pertencer a outra conta."
    nota="Confira o endereço na barra do navegador. Se veio de um e-mail ou mensagem, peça o link de novo a quem enviou."
    secundaria={<Voltar />}
  />
);

export const ServerError = () => (
  <State
    code="500"
    rotuloDoCodigo="Erro 500"
    icone={<Icone.AlertaTriangulo tamanho={13} />}
    title={<>Algo quebrou <mark>do nosso lado</mark>.</>}
    description="Não foi nada que você fez. Em geral basta tentar de novo em alguns instantes."
    nota="Se continuar acontecendo, avise a equipe responsável pela conta e diga o que estava fazendo quando a tela apareceu."
    to="/home"
    cta="Ir para o painel"
    secundaria={
      <Button variant="secondary" onClick={() => window.location.reload()}>
        Tentar de novo
      </Button>
    }
  />
);

/**
 * Conta suspensa. `/settings/billing` é rota `soYard` (só a equipe Yard);
 * mandar o cliente comum para lá devolve um 403 em cima do bloqueio. Então:
 * a equipe vê "Ver plano"; o cliente vê a orientação de falar com quem
 * administra a conta e uma saída real, trocar de conta. Suspensão não é
 * necessariamente cobrança, e o texto não afirma isso.
 */
export const Suspended = () => {
  const { adminPlataforma, signOut } = useAuth();
  const sair = (
    <Button variant="secondary" onClick={() => void signOut()}>
      Sair e entrar com outra conta
    </Button>
  );
  return (
    <State
      code="!"
      rotuloDoCodigo="Conta suspensa"
      icone={<Icone.Pagamento tamanho={13} />}
      title={<>A conta está <mark>em pausa</mark>.</>}
      description="Esta organização está suspensa e o acesso operacional ficou bloqueado. Os dados continuam guardados; quando a conta for reativada, tudo volta como estava."
      nota={
        adminPlataforma
          ? 'Você é da equipe Yard: a suspensão e o plano ficam no console, em Clientes.'
          : 'Fale com quem administra esta conta na sua empresa ou com a equipe Yard para saber o motivo e o que falta para reativar.'
      }
      to={adminPlataforma ? '/settings/billing' : '/home'}
      cta={adminPlataforma ? 'Ver plano' : 'Ir para o painel'}
      secundaria={sair}
      semPrimaria={!adminPlataforma}
    />
  );
};
